import { Core, Decorative } from "@faire/design-tokens";
import { trackBrandLeadsApplyToSellOnFaireClick } from "@faire/web-api/events/brandLeads/click/applyToSellOnFaire";
import { trackBrandLeadsBrandsPageSignInClick } from "@faire/web-api/events/brandLeads/click/brandsPageSignIn";
import { trackBrandLeadsScheduleACallClick } from "@faire/web-api/events/brandLeads/click/scheduleACall";
import { trackBrandLeadsBrandsPageHeaderSectionView } from "@faire/web-api/events/brandLeads/view/brandsPageHeaderSection";
import { ISO3166Alpha3 } from "@faire/web-api/indigofair/iso3166/ISO3166Alpha3";
import apply from "@faire/web-api/routes/apply";
import { getIsoToCountryNames } from "@faire/web/common/consts/ISO_TO_COUNTRY_NAME";
import { useStrictLocalization } from "@faire/web/common/localization";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization/localize";
import { Button } from "@faire/web/slate/Button";
import { Opacity } from "@faire/web/slate/Color";
import { Input } from "@faire/web/slate/Input";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import React, { MutableRefObject, RefObject, useEffect } from "react";
import styled from "styled-components";

import { home } from "@faire/retailer/paths";
import {
  RetailerStore,
  useRetailerStore,
} from "@faire/retailer/stores/domain/RetailerStore";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";
import { BrandsLandingPageState } from "@faire/retailer/views/brands/landing-page/BrandsLandingPageState";

import { CTAPosition, StyledSellOnFaireButton } from "./const";

interface IProp {
  categoryInclusiveReference:
    | MutableRefObject<HTMLDivElement | null>
    | RefObject<HTMLDivElement>;
  viewState: BrandsLandingPageState;
}

const ReferrerInfoBase: React.FC<{ viewState: BrandsLandingPageState }> = ({
  viewState,
}) => {
  if (
    (!viewState.makerApplicationState?.referrerInformation?.name &&
      !viewState.makerApplicationState?.otherReferringRetailerCount) ||
    viewState.makerApplicationState?.loading
  ) {
    return null;
  }

  if (viewState.makerApplicationState?.referrerInformation?.name) {
    if (
      viewState.makerApplicationState?.isM2M ||
      !viewState.makerApplicationState?.otherReferringRetailerCount
    ) {
      return (
        <StrictLocalMsg
          values={DEPRECATED_backwardsCompatibleValues({
            referrerName:
              viewState.makerApplicationState?.referrerInformation.name,
          })}
          defaultMessage="{referrerName} has invited you"
          description={{
            text: "Informative message indicating who referred the prospective client",
          }}
        />
      );
    }
    return (
      <StrictLocalMsg
        values={DEPRECATED_backwardsCompatibleValues({
          referrerName:
            viewState.makerApplicationState?.referrerInformation.name,
          referrerCount:
            viewState.makerApplicationState?.otherReferringRetailerCount,
        })}
        defaultMessage="{referrerName} + {referrerCount} more"
        description={{
          text: "Informative message indicating how many referrers have invited the prospective client",
        }}
      />
    );
  }
  return (
    <StrictLocalMsg
      values={DEPRECATED_backwardsCompatibleValues({
        referrerCount:
          viewState.makerApplicationState?.otherReferringRetailerCount ?? 1,
      })}
      defaultMessage="{referrerCount, plural, one{# more has invited you} other{# more have invited you}}"
      description={{
        text: "Informative message indicating how many referrers have invited the prospective client",
      }}
    />
  );
};

const ReferrerInfo = observer(ReferrerInfoBase);

const BrandsLandingPageTopSectionBase: React.FC<IProp> = (props) => {
  useEffect(() => {
    trackBrandLeadsBrandsPageHeaderSectionView();
  }, []);

  return (
    <Column
      style={{
        backgroundColor: Decorative.shade.yellow,
        width: "100%",
      }}
    >
      <SellOnFaire viewState={props.viewState} />
      <HeaderContent viewState={props.viewState} />

      <Spacer height="4x" ref={props.categoryInclusiveReference} />
      <ResponsiveContentV2 desktopAndAbove={<Spacer height="7x" />} />
    </Column>
  );
};
export const BrandsLandingPageTopSection = observer(
  BrandsLandingPageTopSectionBase
);

const getCountrySpecificHeaderMessage = (
  isoCountryCode: keyof typeof ISO3166Alpha3,
  strictLocalize: StrictLocalizeFunction
): string => {
  switch (isoCountryCode) {
    case "FRA":
      return strictLocalize({
        defaultMessage:
          "Connect with independent retailers from the U.S., France, and beyond",
        description: {
          text: "Promotional message imploring brands to join Faire and connecting with retailers around the world.",
        },
      });
    case "DEU":
      return strictLocalize({
        defaultMessage:
          "Connect with independent retailers from the U.S., Germany, and beyond",
        description: {
          text: "Promotional message imploring brands to join Faire and connecting with retailers around the world.",
        },
      });
    case "ESP":
      return strictLocalize({
        defaultMessage:
          "Connect with independent retailers from the U.S., Spain, and beyond",
        description: {
          text: "Promotional message imploring brands to join Faire and connecting with retailers around the world.",
        },
      });
    case "ITA":
      return strictLocalize({
        defaultMessage:
          "Connect with independent retailers from the U.S., Italy, and beyond",
        description: {
          text: "Promotional message imploring brands to join Faire and connecting with retailers around the world.",
        },
      });
    default:
      return strictLocalize(
        {
          defaultMessage:
            "Connect with independent retailers from the U.S., {FromCountry}, and beyond",
          description: {
            text: "Promotional message imploring brands to join Faire and connecting with retailers around the world.",
          },
        },
        {
          FromCountry:
            // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
            getIsoToCountryNames(strictLocalize)[isoCountryCode],
        }
      );
  }
};
const getGetStartedMessage = (
  isEurope: boolean,
  strictLocalize: StrictLocalizeFunction
) =>
  isEurope
    ? strictLocalize({
        defaultMessage: "Get Started",
        description: {
          text: "Have the prospective clint click this and get started signing up to sell on Faire",
        },
      })
    : strictLocalize({
        defaultMessage: "Start Selling",
        description: {
          text: "Have the prospective clint click this and get started signing up to sell on Faire",
        },
      });

const MobileTitle: React.FC = () => {
  const { isEurope } = useRetailerStore(["isEurope"]);

  return (
    <Column align="flex-start">
      <Typography
        variant={{
          mobileAndAbove: "displaySSerifRegular",
          tabletAndAbove: "displayMSerifRegular",
        }}
        color={Core.text.inverse}
      >
        {isEurope ? (
          <StrictLocalMsg
            defaultMessage="Sell to hundreds of thousands of retailers across Europe and the U.S."
            description={{
              text: "Promotional message imploring brands to join Faire to be able to sell to the large number of retailers on Faire.",
              img: "https://cdn.faire.com/fastly/6a6fb8ebf6300c5a01af596cb34a14bdedb9c178c5a033f944654533e66f553a.png",
            }}
          />
        ) : (
          <StrictLocalMsg
            defaultMessage="Access hundreds of thousands of retailers around the world"
            description={{
              text: "Promotional message imploring brands to join Faire to be able to sell to the large number of retailers on Faire.",
              img: "https://cdn.faire.com/fastly/fca8212d3596367b58159706d76429e7b8ac775e362d031aa75082319e6e522b.png",
            }}
          />
        )}
      </Typography>
      <Spacer height="2x" />
      <BrandsPageSubTitle />
    </Column>
  );
};

const BrandsDesktopTitle: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <Column align="center">
      <DesktopTitle
        variant="displayLSerifRegular"
        color={Core.text.inverse}
        align="center"
      >
        {RetailerStore.get().isEurope ? (
          getCountrySpecificHeaderMessage(
            RetailerStore.get().retailerCountry as ISO3166Alpha3,
            strictLocalize
          )
        ) : (
          <>
            {RetailerStore.get().isCanadian ? (
              <StrictLocalMsg
                defaultMessage="Reach independent retailers across Canada, the US, Europe, and more"
                description={{
                  text: "Promotional message imploring brands to join Faire to be able to sell to the total number of retailers on Faire.",
                }}
              />
            ) : (
              <StrictLocalMsg
                defaultMessage="Reach independent retailers across the US, Canada, Europe, and more"
                description={{
                  text: "Promotional message imploring brands to join Faire to be able to sell to the total number of retailers on Faire.",
                }}
              />
            )}
          </>
        )}
      </DesktopTitle>
      <Spacer height="3x" />
      <BrandsPageSubTitle />
    </Column>
  );
};

const BrandsPageTitle: React.FC = () => {
  return (
    <ResponsiveContentV2
      tabletAndBelow={<MobileTitle />}
      desktopAndAbove={<BrandsDesktopTitle />}
    />
  );
};

const BrandsPageSubTitle: React.FC = () => {
  return (
    <ResponsiveContentV2
      tabletAndBelow={
        <Typography
          variant="paragraphSansRegular"
          align="left"
          color={Core.text.inverse}
        >
          <StrictLocalMsg
            defaultMessage="Sign up to start selling in Faire’s B2B marketplace. No set-up fees. No commitments."
            description={{
              text: "Subheading showcasing Faire's cost and commitment free sign-up process, keep the term B2B in English",
            }}
          />
        </Typography>
      }
      desktopAndAbove={
        <Typography
          variant="subheadingSansRegular"
          align="center"
          color={Core.text.inverse}
        >
          <StrictLocalMsg
            defaultMessage="Sign up to start selling in Faire’s B2B marketplace. No set-up fees. No commitments."
            description={{
              text: "Subheading showcasing Faire's cost and commitment free sign-up process, keep the term B2B in English",
            }}
          />
        </Typography>
      }
    />
  );
};

const SellOnFaireBase: React.FC<{
  viewState: BrandsLandingPageState;
}> = ({ viewState }) => {
  const referrerExists =
    (viewState.makerApplicationState?.referrerInformation?.name ||
      viewState.makerApplicationState?.otherReferringRetailerCount) &&
    !viewState.makerApplicationState?.loading;
  return (
    <ResponsiveContentV2
      tabletAndBelow={<Spacer height="1x" />}
      desktopAndAbove={
        <>
          <Spacer height="5x" />
          {/* title needs to be h1 for SEO purpose. please don't change this before speaking with Retailer SEO team */}
          <Typography
            variant="labelSansRegular"
            color={Core.text.inverse}
            align="center"
            data-test-id="referrer-info"
            as="h1"
          >
            {viewState.isR2BorB2B && referrerExists ? (
              <ReferrerInfo viewState={viewState} />
            ) : (
              <StrictLocalMsg
                defaultMessage="Sell on Faire"
                description={{
                  text: "Have the prospective clint click this and get started signing up to sell on Faire",
                }}
              />
            )}
          </Typography>
          <Spacer height="3x" />
        </>
      }
    />
  );
};

const SellOnFaire = observer(SellOnFaireBase);

const SignInLink = (chunk: string) => {
  const signInUrl = UserStore.get().userToken ? home() : home({ signIn: "1" });
  return (
    <EmbeddedLink
      color={Core.text.inverse}
      onClick={() =>
        trackBrandLeadsBrandsPageSignInClick(CTAPosition.TOP_SECTION)
      }
      linkTo={signInUrl}
    >
      {chunk}
    </EmbeddedLink>
  );
};

const HeaderContentBase: React.FC<{
  viewState: BrandsLandingPageState;
}> = ({ viewState }) => {
  return (
    <>
      <BrandsPageTitleWrapper>
        <BrandsPageTitle />
      </BrandsPageTitleWrapper>
      <Spacer
        height={{
          mobileAndAbove: "3x",
          tabletAndAbove: "7x",
        }}
      />

      {viewState.isPersonalized ? (
        <PersonalizedHeaderContent viewState={viewState} />
      ) : (
        <UnpersonalizedHeaderContent viewState={viewState} />
      )}
      <Spacer height="3x" />
      <AlreadyOnFaireWrapper>
        <Typography variant="paragraphSansRegular" color={Core.text.inverse}>
          <StrictLocalMsg
            defaultMessage="Already on Faire? <SignInLink>Sign In</SignInLink>"
            description={{
              text: "Click to sign in to your account",
            }}
            values={DEPRECATED_backwardsCompatibleValues({
              SignInLink,
            })}
          />
        </Typography>
      </AlreadyOnFaireWrapper>
    </>
  );
};

const HeaderContent = observer(HeaderContentBase);

const MobileUnpersonalizedHeaderContentBase: React.FC<{
  viewState: BrandsLandingPageState;
}> = ({ viewState }) => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <Column
      style={{ width: "100%", padding: getSpacing(0, "3x") }}
      align="flex-start"
      justify="left"
    >
      <ContainerForm action={apply()} onSubmit={viewState.goToForm}>
        <TopSectionEmailInputField
          type="text"
          name="email_address"
          placeholder={strictLocalize({
            defaultMessage: "Email address",
            description: {
              text: "Input placeholder prompting the user to input their email address.",
            },
          })}
          autoComplete="email"
          value={viewState.emailAddress}
          onInput={viewState.handleEmailChange}
          data-test-id="top-section-email-input-mobile"
        />
        <Spacer height="2x" />
        <TopSectionApplyButton
          onClick={() => {
            trackBrandLeadsApplyToSellOnFaireClick(CTAPosition.TOP_SECTION);
          }}
          data-test-id="top-section-get-started-mobile-button"
          type="submit"
        >
          {getGetStartedMessage(RetailerStore.get().isEurope, strictLocalize)}
        </TopSectionApplyButton>
      </ContainerForm>
    </Column>
  );
};

const MobileUnpersonalizedHeaderContent = observer(
  MobileUnpersonalizedHeaderContentBase
);

const DesktopUnpersonalizedHeaderContentBase: React.FC<{
  viewState: BrandsLandingPageState;
}> = ({ viewState }) => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <ContainerForm action={apply()} onSubmit={viewState.goToForm}>
      <div>
        <TopSectionEmailInputField
          type="text"
          name="email_address"
          placeholder={strictLocalize({
            defaultMessage: "Email address",
            description: {
              text: "Input placeholder prompting the user to input their email address.",
            },
          })}
          autoComplete="email"
          value={viewState.emailAddress}
          onChange={viewState.handleEmailChange}
          data-test-id="top-section-email-input-desktop"
        />
      </div>
      <TopSectionApplyButton
        onClick={() => {
          trackBrandLeadsApplyToSellOnFaireClick(CTAPosition.TOP_SECTION);
        }}
        data-test-id="top-section-get-started-desktop-button"
        type="submit"
      >
        {getGetStartedMessage(RetailerStore.get().isEurope, strictLocalize)}
      </TopSectionApplyButton>
    </ContainerForm>
  );
};

const DesktopUnpersonalizedHeaderContent = observer(
  DesktopUnpersonalizedHeaderContentBase
);

const UnpersonalizedHeaderContentBase: React.FC<{
  viewState: BrandsLandingPageState;
}> = ({ viewState }) => {
  return (
    <ResponsiveContentV2
      mobile={<MobileUnpersonalizedHeaderContent viewState={viewState} />}
      tabletAndAbove={
        <DesktopUnpersonalizedHeaderContent viewState={viewState} />
      }
    />
  );
};

const UnpersonalizedHeaderContent = observer(UnpersonalizedHeaderContentBase);

const PersonalizedHeaderContent: React.FC<{
  viewState: BrandsLandingPageState;
}> = ({ viewState }) => {
  return (
    <Row style={{ width: "100%" }} justify="center">
      <StyledSellOnFaireButton
        linkTo={viewState.calendlyLink}
        target="_blank"
        data-test-id="top-section-schedule-call-button"
        onClick={() => {
          trackBrandLeadsScheduleACallClick(CTAPosition.TOP_SECTION);
        }}
      >
        <StrictLocalMsg defaultMessage="Schedule a Call" />
      </StyledSellOnFaireButton>
    </Row>
  );
};

const AlreadyOnFaireWrapper = styled(Column).attrs({
  justify: { mobileAndAbove: "left", tabletAndAbove: "center" },
  align: { mobileAndAbove: "flex-start", tabletAndAbove: "center" },
})`
  padding: ${getSpacing(0, "3x")};
  width: 100%;
`;

const DesktopTitle = styled(Typography)`
  max-width: 780px;
`;

const TopSectionApplyButton = styled(Button).attrs({
  variant: "secondary",
})`
  width: max-content;
  height: 50px;
  color: ${Core.text.default};
  background-color: ${Core.surface.inverse};
  border: 1.2px solid ${Core.border.muted};

  ${responsiveMediaQueries.mobile} {
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
`;

const BrandsPageTitleWrapper = styled(Column).attrs({
  justify: { mobileAndAbove: "left", tabletAndAbove: "center" },
  align: { mobileAndAbove: "flex-start", tabletAndAbove: "center" },
})`
  width: 100%;
  padding: ${getSpacing(0, "3x")};
`;

const ContainerForm = styled.form`
  width: 100%;
  justify-content: center;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-content: center;

  ${responsiveMediaQueries.mobile} {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const TopSectionEmailInputField = styled(Input)`
  width: 280px;
  height: 50px;
  border: 1.2px solid ${Core.border.muted};
  background: none;

  input {
    color: ${Core.text.inverse};

    &::placeholder {
      color: ${Core.text.inverse};
    }
  }

  &:focus,
  &:focus-within {
    border-color: ${Core.border.muted};

    input {
      &::placeholder {
        color: ${Core.text.inverse + Opacity.O_50};
      }
    }
  }

  ${responsiveMediaQueries.mobile} {
    height: 40px;
    width: 100%;
  }
`;
